/**
 * The picker's request bus.
 *
 * Two things can ask for a source — the patched `getDisplayMedia` in this app,
 * and the main process (anything that reached Electron's display-media handler
 * without going through the patch). Both land on one queue so there is never
 * more than one picker on screen.
 */
import { createStore } from '@/lib/tinyStore';
import { desktopBridge } from './bridge';

export interface ScreenPickerState {
  readonly open: boolean;
  /** Set when the request came from the main process and needs a reply there. */
  readonly requestId: string | null;
}

type Resolver = (sourceId: string | null) => void;

export const screenPickerStore = createStore<ScreenPickerState>({ open: false, requestId: null });

let resolver: Resolver | null = null;

/** Opens the picker and resolves with the chosen source id, or null if cancelled. */
export function requestScreenSource(requestId: string | null = null): Promise<string | null> {
  /* A second request while one is open cancels the first: two overlapping
     pickers would leave one promise hanging forever. */
  resolver?.(null);
  screenPickerStore.set({ open: true, requestId });
  return new Promise<string | null>((resolve) => {
    resolver = resolve;
  });
}

/** Called by the picker UI. `null` = the user cancelled. */
export function settleScreenSource(sourceId: string | null): void {
  const { requestId } = screenPickerStore.get();
  screenPickerStore.set({ open: false, requestId: null });

  if (requestId !== null) {
    const bridge = desktopBridge();
    void bridge?.respondScreenSource(requestId, sourceId).catch(() => undefined);
  }

  const settle = resolver;
  resolver = null;
  settle?.(sourceId);
}

export const selectPickerOpen = (state: ScreenPickerState): boolean => state.open;
